<template>
  <div class="tw-flex tw-flex-wrap tw-gap-2" :data-testid="testId">
    <NoteTypesFilter
      v-if="noteTypeOptions?.length"
      :options="noteTypeOptions"
    />

    <TagsFilter v-if="tagOptions?.[0]?.options?.length" :options="tagOptions" />

    <AuthorsFilter
      v-if="authorOptions?.length && showAuthorsFilter"
      :options="authorOptions"
    />

    <SearchFilter />

    <div
      v-if="showClearAll"
      data-testid="inbox-filters-clear-all"
      class="tw-mt-[2px]"
      @click="clearAllFilters"
    >
      <span class="fa fa-remove sf-pointer tw-pt-3 tw-pl-2 tw-pr-0 tw-ml-0" />
      <span class="tw-pt-2 sf-pointer">
        {{ I18n.t('inbox.filters.clear') }}
      </span>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { useInboxFiltersStore } from '../store/inbox-filters'
import NoteTypesFilter from './note-types-filter.vue'
import TagsFilter from './tags-filter.vue'
import AuthorsFilter from './authors-filter.vue'
import SearchFilter from './search-filter.vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { useStore } from 'vuex'
import { NoteTypeOption } from '@app/application/communication/screens/inbox/store/constants'

const props = defineProps({
  showAuthorsFilter: {
    type: Boolean,
    default: true,
  },
  initialSearch: {
    type: String,
    default: '',
  },
  initialNoteType: {
    type: String,
    default: null,
  },
  initialTags: {
    type: Array,
    default: () => [],
  },
  initialAuthors: {
    type: Array,
    default: () => [],
  },
})

const store = useStore()
const I18n = useI18n()
const inboxFiltersStore = useInboxFiltersStore()

const testId = computed(() => {
  if (inboxFiltersStore.receivedInitialValues) {
    return 'inbox-filters-initialized'
  } else {
    return 'inbox-filters-uninitialized'
  }
})

const noteTypeOptions = [
  {
    id: 'mentions',
    options: [
      {
        label: I18n.t('inbox.filters.note_types.labels.mentions'),
        value: NoteTypeOption.MENTIONS,
      },
    ],
  },
  {
    id: 'note-types',
    options: [
      {
        label: I18n.t('inbox.filters.note_types.labels.todos'),
        value: NoteTypeOption.TODO,
        icon: 'bolt',
        iconColor: 'sf-success-light-1',
      },
      {
        label: I18n.t('inbox.filters.note_types.labels.open_todos'),
        value: NoteTypeOption.OPEN_TODO,
        icon: 'bolt',
        iconColor: 'sf-success-light-1',
      },
      {
        label: I18n.t('inbox.filters.note_types.labels.notes'),
        value: NoteTypeOption.NOTE,
        icon: 'comment',
        iconColor: 'sf-neutral-dark-1',
      },
    ],
  },
]

const tagOptions = computed(() => {
  const options = []
  for (const tag of Object.values(store.state.remarkTags.allRemarkTags)) {
    if (!tag.archived) {
      options.push({ label: tag.name, value: tag.id })
    }
  }
  return [{ id: 'tags', options }]
})

const authorOptions = computed(() => {
  const specialOptions = [
    {
      label: I18n.t('inbox.filters.authors.me'),
      value: Number.parseInt(Silverfin.currentUserId()),
      icon: 'user',
      iconColor: 'sf-neutral-dark-1',
    },
  ]
  if (!store.getters.userById(Silverfin.currentUserId())?.external) {
    specialOptions.push({
      label: I18n.t('inbox.filters.authors.external'),
      value: 'external',
    })
  }
  const otherUsers = []
  for (const user of Object.values(store.state.users.users)) {
    if (`${user.id}` !== Silverfin.currentUserId()) {
      otherUsers.push({ label: user.name, value: user.id })
    }
  }
  return [
    { id: 'special', options: specialOptions },
    { id: 'users', options: otherUsers },
  ]
})

const showClearAll = computed(() => {
  return (
    inboxFiltersStore.searchTerm ||
    inboxFiltersStore.tags.length ||
    inboxFiltersStore.authors.length ||
    inboxFiltersStore.noteType
  )
})

onMounted(() => {
  inboxFiltersStore.receiveFilters(
    props.initialNoteType,
    props.initialTags,
    props.initialAuthors,
    props.initialSearch
  )
})

function clearAllFilters() {
  inboxFiltersStore.clearFilters()
}
</script>
